// import parseNumber from "@tSurya/utilities/parseNumber";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import Color from "color";
import parseInt from "lodash/parseInt";
import { css } from "styled-components";

const STATIC_PATH = "/userfiles/_static/store";

// -- Colors

const swatch = {
    white: "#FFFFFF",
    celtic: "#2E3F2F",
    black: "#323132",
    lightBlack: "#333333",
    lightBrown: "#866A5B",
    green: "#8AD902",
    red: "#D9001B",
    ocean: "#00558e",
    neutralLight: "#F0F2F7",
    neutralMedium: "#CACFDE",
    primaryBlue: "#006ED2",
    secondaryNavyOne: "#134078",
    secondaryNavyThree: "#112945",
    solitude: "#F0F2F7",
    darkCyanBlue: "#112945",
    yellow: "#EDB800",
    lightGreen: "#00B900",
    alertMsgRed: "#ED0000",
    rockBlue: "#9DB1C9",
    londonFog: "#A29D92",

    grey: {
        lighter: "#F6F6F6",
        light: "#F3F5F7",
        base: "#636161",
        dark: "#636363",
        darker: "#323132",
        shade: "#F1F6FB",
        grayShade: "#F2F2F2",
        lightGrey: "#F9F9F9",
        darkGrey: "#F1F2F3",
        brightGrey: "#CCCCCC",
        whisperGrey: "#ebebeb",
    },
};

export const COLORS = {
    white: swatch.white,
    celtic: swatch.celtic,
    black: swatch.black,
    lightBrown: swatch.lightBrown,
    lightBlack: swatch.lightBlack,
    green: swatch.green,
    ocean: swatch.ocean,
    red: swatch.red,
    neutralLight: swatch.neutralLight,
    neutralMedium: swatch.neutralMedium,
    primaryBlue: swatch.primaryBlue,
    secondaryNavyOne: swatch.secondaryNavyOne,
    secondaryNavyThree: swatch.secondaryNavyThree,
    solitude: swatch.solitude,
    darkCyanBlue: swatch.darkCyanBlue,
    yellow: swatch.yellow,
    lightGreen: swatch.lightGreen,
    alertMsgRed: swatch.alertMsgRed,
    rockBlue: swatch.rockBlue,
    lightGrey: swatch.grey.lightGrey,
    lighterGrey: "#B7B7B7",
    darkGrey: swatch.grey.darkGrey,
    darkerGrey: swatch.grey.dark,
    brightGrey: swatch.grey.brightGrey,
    whisperGrey: swatch.grey.whisperGrey,
    dark: swatch.grey.dark,
    londonFog: swatch.londonFog,
    darkBlack: "#000000",
    secondaryBtn: "#0D70B7",
    secondaryBg: "#EDF4F8",
    Matterhorn: "#545454",
    selectedButton: "#C67D30",
    carouselBg: "#F8F8F8",
    categoryTitle: "#323132",
    filterCheckboxText: "#545454",
    featuredMobile: "#2C2420",
    floralWhite: "#F8F7F4",
    accordionBorder: "#D5CEBE",
    primary: {
        base: "#323132",
        contrast: "#ffffff",
    },
    secondary: {
        base: "#F4F3F1",
        contrast: "#323132",
    },
    accent: {
        main: "#866A5B",
        contrast: "#FFFFFF",
    },
    tertiary: {
        light: "#676767",
        base: "#ED9C00",
        dark: "#676767",
        contrast: "black",
    },
    success: "#33864F",
    warning: "#EF9600",
    error: "#BE3445",
    darkmagenta: "#323132",
    background: {
        main: swatch.white,
        contrast: "#0E0E0E",
        black: swatch.black,
        grey: "rgba(255, 255, 255, 0.05)",
        light: "#F4F3F1",
        lighter: "#f8f8f8",
    },
    border: {
        main: "#E1DDE2",
    },
    disabled: {
        main: "#E5E5E5",
    },
    info: {
        main: "#1C355E",
        contrast: "#FFFFFF",
    },
    text: {
        accent: "#323132",
        disabled: "#C5C5C7",
        main: "#333333",
        link: "#866A5B",
        white: "#FFFFFF",
    },
    shadow: {
        light: "rgba(48, 49, 51, 0.05)",
        base: "rgba(48, 49, 51, 0.1)",
    },
    pagination: {
        base: swatch.grey.light,
        hover: Color(swatch.grey.light).darken(0.15).hex(),
    },
    outline: "#1d8ed5",
    grayBg: "rgb(241, 239, 234)",
    lightGrayBg: "rgb(248, 248, 248)",
    golden: "#A07400",
    filterIcon: {
        primary: {
            base: "#323132",
            contrast: "#FFFFFF",
        },
    },
    checkout: {
        text: {
            primary: "#1C1917",
            secondary: "#494745",
        },
        sectionHeaderBg: "#F5F5F5",
        lightBg: "#F8F8F8",
    },
    onyx: {
        300: "#777574",
    },
    gray: {
        50: "#FAFAFA",
        100: "#F2F2F2",
        200: "#E5E5E5",
        300: "#CCCCCC",
        400: "#B3B3B3",
        500: "#999999",
        600: "#808080",
        700: "#666666",
        800: "#4D4D4D",
        900: "#333333",
    },
};

// -- Typography

// const fallbackFont = "Poppins";

export const TYPOGRAPHY = {
    fontFamilyImportUrl: "https://fonts.googleapis.com/css2?family=Mulish:wght@200;300;400;500;600;700;800;900&display=swap",
    fontFamily: {
        title: `'Mulish'`,
        base: `'Mulish'`,
        heading: `'Mulish'`,
        importUrl: "https://fonts.googleapis.com/css2?family=Mulish:wght@200;300;400;500;600;700;800;900&display=swap",
    },
    weight: {
        base: 400,
        baseBold: 500,
        mediumbold: 600,
        bold: 700,
        black: 800,
    },
    size: {
        veryTiny: "10px",
        vTiny: "11px",
        tiny: "12px",
        smallest: "13px",
        smaller: "14px",
        small: "15px",
        base: "16px",
        large: "18px",
        larger: "20px",
        largest: "24px",
        xlargest: "26px",
        xxlarge: "28px",
        xxxlarge: "32px",
        xlarge: "30px",
        xxlargest: "46px",
    },
    lineHeight: {
        smaller: 1,
        small: 1.25,
        large: 2,
        ssmall: "16px",
        msmall: "18px",
        lsmall: "20px",
        base: "22px",
        xbase: "24px",
        medium: "26px",
        lmedium: "28px",
        xmedium: "32px",
        xxmedium: "34px",
        xlmedium: "36px",
        larger: "38px",
        largest: "42px",
        xlargest: "46px",
    },
    spacing: {
        small: "0.5em",
        base: "1em",
        large: "1.5em",
        larger: "2em",
        largest: "3em",
    },
    announcementBar: {
        fontSize: "14px",
        mobileFontSize: "12px",
        fontWeight: 500,
        lineHeight: "16px",
        textTransform: "uppercase" as const,
        letterSpacing: "0",
    },
    breadcrumbs: {
        weight: 400,
        current: {
            weight: 700,
        },
    },
    body: {
        size: "15px",
        weight: 400,
        fontFamily: "'Mulish'",
        lineHeight: "24px",
        letterSpacing: "normal",
    },
    p: {
        size: "14px",
        weight: 400,
        fontFamily: "'Mulish'",
        lineHeight: "22px",
        letterSpacing: "normal",
    },
    h1: {
        size: 36,
        weight: 600,
        lineHeight: "42px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 24px;
                        line-height: 30px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    h2: {
        size: 28,
        weight: 600,
        lineHeight: "38px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
    },
    h3: {
        size: 24,
        weight: 600,
        lineHeight: "34px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
    },
    h4: {
        size: 20,
        weight: 700,
        lineHeight: "28px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
    },
    h5: {
        size: 18,
        weight: 700,
        lineHeight: "26px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
    },
    h6: {
        size: 16,
        weight: 700,
        lineHeight: "24px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
    },
    headerPrimary: {
        size: 15,
        fontFamily: "'Mulish'",
        color: "text.main",
        transform: "none" as const,
        weight: 600,
        lineHeight: "22px",
    },
    headerSecondary: {
        size: 15,
        fontFamily: "'Mulish'",
        transform: "none" as const,
        weight: 600,
        lineHeight: "22px",
    },
    headerTertiary: {
        size: 14,
        fontFamily: "'Mulish'",
        color: "text.main",
        transform: "uppercase" as const,
        lineHeight: "20px",
        weight: 400,
    },
    legend: {
        size: 12,
        color: "text.accent",
        transform: "uppercase" as const,
        weight: 600,
    },
    link: {
        size: 15,
        weight: 700,
        lineHeight: "22px",
    },
    header: {
        size: "12px",
        textTransform: "uppercase",
        letterSpacing: "1px",
        lineHeight: "20px",
        weight: 600,
    },
    openTradeAccountLabel: {
        textTransform: "uppercase",
    },
    cartLink: {
        size: "12px",
        textTransform: "uppercase",
        weight: 400,
    },
    signInTitle: {
        size: "12px",
        textTransform: "uppercase",
        weight: 400,
    },
    megamenuAccentLink: {
        weight: 700,
    },
    megaMenuHeading: {
        size: "15px",
        weight: 700,
        letterSpacing: "normal",
        lineHeight: "22px",
        textTransform: "none" as const,
        color: COLORS.text.accent,
    },
    megaMenuLink: {
        size: "14px",
        weight: 400,
        letterSpacing: "normal",
        lineHeight: "20px",
        color: COLORS.text.accent,
    },
    footerHeadings: {
        textTransform: "uppercase",
        size: "15px",
        weight: 700,
        fontFamily: "'Mulish'",
        fontStyle: "normal",
        lineHeight: "24px",
    },
    footerLinks: {
        textTransform: "capitalize",
        color: getColor("primary.contrast"),
        letterSpacing: "normal",
    },
    footerEndLine: {
        size: "15px",
        weight: 600,
        lineHeight: "22px",
        fontFamily: "'Mulish'",
        fontStyle: "normal",
        letterSpacing: "normal",
        color: getColor("primary.contrast"),
        opacity: 0.6,
    },
    mobileBrandNavigationLink: {
        size: "10px",
    },
    mobileNavCategoryLinks: {
        size: "15px",
        weight: 500,
        lineHeight: "22px",
        letterSpacing: "normal",
    },
    mobileViewLinksList: {
        size: "15px",
        weight: 700,
        lineHeight: "22px",
    },
    button: {
        size: "16px",
        weight: 700,
        lineHeight: "32px",
        fontFamily: "'Mulish'",
    },
    pageTitle: {
        size: "30px",
        weight: 600,
        lineHeight: "42px",
        fontStyle: "normal",
        fontFamily: "'Mulish'",
        textTransform: "uppercase",
    },
    headings: {
        weight: 700,
        fontStyle: "normal",
        fontFamily: "'Mulish'",
        textTransform: "uppercase",
    },
    widgetTitle: {
        size: "28px",
        lineHeight: "36px",
        weight: 600,
        textTransform: "uppercase",
        fontStyle: "normal",
        fontFamily: "'Mulish'",
        mobileSize: "24px",
        mobileLineHeight: "36px",
    },
    badge: {
        size: "15px",
        lineHeight: "24px",
        weight: 400,
        fontFamily: "'Mulish'",
        textTransform: "capitalize",
    },
    heroCarousel: {
        size: "40px",
        lineHeight: "56px",
        fontFamily: "'Mullish'",
        fontStyle: "italic",
        mobileSize: "32px",
        mobileLineHeight: "42px",
    },
    cardSectionTitle: {
        size: "18px",
    },
    filterButton: {
        size: "16px",
        weight: 400,
        lineHeight: "normal",
        fontFamily: "'Mulish'",
        letterSpacing: "normal",
    },
    sortSelectLabel: {
        size: "15px",
        weight: 400,
        lineHeight: "24px",
        fontFamily: "'Mulish'",
        letterSpacing: "normal",
    },
    sortSelectInput: {
        size: "16px",
        weight: 400,
        lineHeight: "24px",
        fontFamily: "'Mulish'",
        letterSpacing: "normal",
    },
    topCategoryFilter: {
        size: "16px",
        weight: 400,
        fontFamily: "'Mulish'",
        lineHeight: "24px",
        letterSpacing: "0.5px",
        color: "#494745",
    },
    productCard: {
        brand: {
            size: "16px",
            weight: 700,
            fontFamily: "'Mulish'",
            lineHeight: "24px",
            letterSpacing: "normal",
        },
        linkTitle: {
            weight: 400,
        },
    },
    accordionItem: {
        size: "14px",
        weight: 700,
        lineHeight: "22px",
        fontFamily: "'Mulish'",
        textTransform: "uppercase",
        sizeSmaller: "15px",
    },
    categoryDetailsLinksList: {
        weight: 700,
        size: "16px",
    },
    categoryCard: {
        name: {
            size: "18px",
            weight: 700,
        },
    },
    productDetails: {
        variantButton: {
            activeWeight: 400,
            size: "15px",
        },
        inventoryMessage: {
            weight: 700,
        },
        accordionButton: {
            fontFamily: "'Mulish'",
            size: "18px",
            weight: 700,
            lineHeight: "26px",
            textTransform: "capitalize",
            letterSpacing: "normal",
        },
        addToCartButton: {
            size: "15px",
            weight: 700,
            letterSpacing: "normal",
            fontFamily: "'Mulish'",
            lineHeight: "32px",
            fontWeight: 700,
        },
        priceText: {
            size: 24,
            weight: 700,
        },
    },
};

// -- Spacings
export const SPACING = {
    xxxlarge: "4em",
    xxlarge: "3em",
    xlarge: "2em",
    large: "1.5em",
    medium: "1.25em",
    standard: "1em",
    small: ".75em",
    xsmall: ".5em",
    xxsmall: ".25em",
    none: "0",
};

// -- Product

const productImageSizes = {
    small: {
        width: 311,
        height: 193,
    },
    medium: {
        width: 690,
        height: 429,
    },
    large: {
        width: 1164,
        height: 724,
    },
};

export const PRODUCT = {
    image: {
        ...productImageSizes,
        ratio: productImageSizes.large.height / productImageSizes.large.width,
    },
};

// -- Common

const contentWidth = 1360;

const spacing = {
    side: ["20px", "30px", "45px", "45px", "45px"],
    content: ["25px", "30px", "45px", "16px", "60px"],
    narrowContent: ["25px", "30px", "40px", "40px", "50px"],
};

const largestSideSpacing = spacing.side.filter(x => x).pop() as any;
const siteWidth = contentWidth + (parseInt(largestSideSpacing) ?? 0) * 2;
// siteWidth = 1280;

export const COMMON = {
    breakpoints: [0, 576, 768, 992, 1440],
    width: {
        content: contentWidth,
        site: "100%",
    },
    layout: {
        mobileSpacing: "40px",
        tabletSpacing: "40px",
        desktopSpacing: "80px",
    },
    spacing,
    outline: {
        width: "2px",
        style: "solid",
    },
};

// -- Menu

export const MENU = {
    mobileButtonSpacing: ".25em",
    topNavItemSpacing: ".8em",
};

// -- Form

export const FORM = {
    field: {
        padding: "1em",
        spacing: "1em",
    },
    button: {
        spacing: "1.6em",
    },
};

export const ELEMENTS = {
    button: {
        base: {
            padding: "10px 34px",
            borderRadius: "0",
        },
        link: {
            letterSpacing: "normal",
        },
    },
    radio: {
        size: "20px",
        dotSize: "10px",
        dotOffset: "5px",
        borderColor: COLORS.primary.base,
        checkedBackground: COLORS.primary.base,
    },
    selectIconSize: "14px",
    cartLink: {
        height: "50px",
    },
    logo: {
        maxWidth: "150px",
    },
    navigationItemDesktop: {
        padding: "12px 8px",
    },
    megamenu: {
        boxShadow: "0 8px 6px -6px rgba(0, 0, 0, 0.05);",
        padding: "50px 15px",
        gridPadding: "16px",
        columnGap: "20px",
        justifyContent: "flex-start",
    },
    menuItem: {
        marginBottom: "0",
        marginTop: "0",
    },
    carouselArrows: {
        size: "36px",
        boxShadow: "0 1px 3px 0 rgba(#A6AFC3, 0.40)",
        border: "1px solid #F5F5F5",
        padding: "0 21px",
        iconTop: "0.125em",
    },
    footerLinks: {
        marginBottom: "0",
    },
    formField: {
        borderRadius: "0",
    },
    filterButton: {
        padding: "8px 16px",
        border: "1px solid #D2D1D1",
        borderRadius: "2px",
        height: "40px",
        borderColor: "#D2D1D1",
    },
    sortSelect: {
        height: "40px",
        marginRight: "20px",
    },
    accordionContainer: {
        desktop: {
            paddingLeft: "20px",
            marginTop: "47px",
        },
        mobile: {
            paddingLeft: "20px",
        },
    },
    accordionItem: {
        borderBottom: "1px solid #E0E0E0 !important",
    },
    filterCheckbox: {
        checkedBackground: "#323132",
    },
    cardContainerStyles: {
        cardDivider: {
            // Two cards per row on mobile, sharing the 14px column-gap applied by the card list.
            width: "calc((100% - 14px) / 2)",
        },
    },
    categoryDetailsLinksList: {
        paddingLeft: "20px",
    },
    gridViewClickable: {
        marginRight: "20px",
    },
    cardContainerMultiColumnGridItem: {
        padding: "10px 22px",
        paddingLeft: "0",
    },
    cardList: {
        gridContainerOffset: {
            marginLeft: "0",
            marginRight: "0",
            width: "100%",
            mobile: {
                marginLeft: "0",
                marginRight: "0",
                width: "100%",
            },
            swatchMobile: {
                marginLeft: "-7px",
                marginRight: "-7px",
                width: "calc(100% + 14px)",
            },
        },
    },
    productListing: {
        heading: {
            marginBottom: "26px",
        },
        totalSortContainer: {
            marginTop: "9px",
        },
    },
    productDetails: {
        variantButton: {
            padding: "8px 15px",
            borderRadius: "0",
            activeBorder: "2px solid #323232",
            activeBackground: "#f8f8f8",
        },
        accordionSection: {
            marginBottom: "40px",
            marginBottomMobile: "14px",
        },
        accordionButton: {
            backgroundColor: "#F8F8F8",
            padding: "10px 20px",
            expandedBorderBottom: "1px solid #E0E0E0",
            borderTop: "1px solid #E0E0E0",
            borderBottom: "1px solid #E0E0E0",
            borderLeft: "1px solid #E0E0E0",
            borderRight: "1px solid #E0E0E0",
        },
        accordionPanel: {
            paddingBottom: "0",
        },
        accordionPanelUnorderedList: {
            padding: "20px 0 0 40px",
            paddingTop: "20px",
        },
        accordionDocumentsWrapper: {
            padding: "20px",
        },
        customproducttilewrapper: {
            padding: "0 30px",
        },
        addToCartButton: {
            width: "auto",
            height: "40px",
        },
        addToProjectButton: {
            width: "auto",
            iconSize: 15,
        },
        msrpWrapper: {
            margin: "0 10px 17.33px 0",
        },
        msrpContainer: {
            color: "#BDBDBD",
            size: "14px",
            marginRight: "2px",
        },
        productPriceWrapper: {
            marginRight: "10px",
            marginTop: "17.33px",
            marginBottom: "17.33px",
        },
        dataPointContent: {
            size: "15px",
            weight: 400,
            letterSpacing: "normal",
            color: "#333333",
        },
        productDataPoint: {
            titleGridItem: {
                size: "24px",
                margin: "0 0 8.33px 0",
            },
            productName: {
                size: "15px",
                margin: "0",
                weight: 700,
                lineHeight: "11px",
            },
        },
        mobileViewAddToCartMobileDiv: {
            display: "inline-block",
        },
        productAvailabilityQuantity: {
            desktop: {
                marginTop: "17px",
            },
            mobile: {
                marginTop: "24px",
            },
        },
        topGridItem: {
            desktop: {
                padding: "15px 15px 0 15px",
                marginTop: "20px",
            },
            mobile: {
                padding: "15px 15px 0 15px",
                marginTop: "17px",
            },
        },
        rightColumnGridItem: {
            desktop: {
                paddingLeft: "6px",
            },
            mobile: {
                paddingLeft: "6px",
            },
        },
        priceContainer: {
            marginTop: "0",
            marginBottom: "0",
        },
        productImageGallery: {
            wrapperMaxHeight: "610px",
            mainWrapperBg: "#f8f8f8",
            thumbBg: "#FAF8F5",
        },
    },
    breadcrumbs: {
        padding: "0",
    },
    productImage: {
        padding: "16px 14px",
    },
    pagination: {
        padding: "0 8px",
        size: "15px",
        minHeight: "40px",
    },
    categoryDetailBanner: {
        textAlign: "left",
        color: "#494745",
        maxWidth: "100%",
        size: "15px",
        letterSpacing: "normal",
        lineHeight: "1.5",
        margin: "8px 0 0",
    },
    categoryListing: {
        mainContentContainer: {
            marginTop: "32px",
        },
        pageTitle: {
            marginBottom: "35px",
        },
    },
};

export const WIDGETS = {
    myListsDetails: {
        quickAddMobileLeftPadding: "5px",
        quickAddCloseIconMobileMarginTop: "21px",
    },
    promotionSummary: {
        fontFamily: "'Mulish', sans-serif",
        discountFontSize: "14px",
        discountValueFontSize: "15px",
        labelFontSize: "15px",
        valueFontSize: "15px",
        messageFontSize: "13px",
        messageWhiteSpace: "nowrap",
        savingsColor: COLORS.success,
        messageColor: "#777777",
        calloutBackground: "#EFF8F1",
        calloutBorder: "#DCEFE0",
        calloutTextColor: COLORS.success,
        calloutMarginTop: "4px",
        calloutGap: "8px",
        calloutPadding: "10px 12px",
        calloutIconSize: "16px",
        calloutFontSize: "13px",
        calloutLineHeight: "1.4",
        dividerColor: "#E0E0E0",
        dividerMargin: "4px 0",
        estimatedTotalFontSize: "15px",
        estimatedTotalFontWeight: 700,
        estimatedTotalLineHeight: "1.4",
        estimatedTotalHelperFontSize: "13px",
        estimatedTotalHelperLineHeight: "1.4",
        estimatedTotalHelperMarginTop: "2px",
    },
    checkoutNavigation: {
        iconWidth: "21px",
        iconHeight: "8px",
        iconGap: "8px",
        backButtonColor: COLORS.text.link,
        desktopBackMarginTop: "30px",
    },
    checkoutPaymentInformation: {
        hostedPaymentField: {
            height: "48px",
            inlineEndInset: "1px",
        },
        summaryAllocation: {
            borderTop: `1px solid ${COLORS.border.main}`,
            margin: "0 20px",
            padding: "12px 0 16px",
            rowGap: "8px",
            labelAmountGap: "12px",
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "1.4",
            dueTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
        },
        paymentBreakdown: {
            optionGap: "12px",
            optionBorder: `1px solid ${COLORS.border.main}`,
            optionSelectedBorder: `1px solid ${COLORS.accent.main}`,
            optionSelectedBoxShadow: `0 0 0 1px ${COLORS.accent.main}`,
            boxBorderRadius: "6px",
            optionHeaderPadding: "16px 20px 16px 28px",
            optionHeaderMobilePadding: "14px 12px 14px 20px",
            optionLabelGap: "16px",
            optionTextMinWidth: "180px",
            optionTitleFontWeight: 700,
            optionTitleLineHeight: "1.3",
            optionSubtitleFontSize: "12px",
            optionSubtitleLineHeight: "1.3",
            optionAmountFontSize: "18px",
            optionAmountMobileFontSize: "15px",
            optionAmountFontWeight: 700,
            optionAmountLineHeight: "1.3",
            selectedContentGap: "8px",
            selectedContentPadding: "0 20px 20px 40px",
            selectedContentMobilePadding: "0 12px 16px 16px",
            chargedTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
            compactRowGap: "16px",
            compactRowPadding: "12px 16px",
            compactRowMobilePadding: "12px",
            compactRowBackground: COLORS.checkout.lightBg,
            compactRowAccentWidth: "4px",
            compactMetaFontSize: "11px",
            compactMetaFontWeight: 700,
            compactMetaLetterSpacing: "0.06em",
            compactMetaLineHeight: "1.3",
            compactLabelMargin: "2px 0 0",
            compactLabelFontSize: "12px",
            compactLabelLineHeight: "1.3",
            compactAmountFontSize: "16px",
            compactAmountFontWeight: 700,
            compactAmountLineHeight: "1.3",
            disclosureMinHeight: "44px",
            disclosurePadding: "8px 0",
            disclosureColor: COLORS.text.link,
            disclosureFontSize: "13px",
            disclosureFontWeight: 700,
            disclosureLineHeight: "1.4",
            disclosureUnderlineOffset: "3px",
            disclosureFocusOutline: `2px solid ${COLORS.accent.main}`,
            disclosureFocusOutlineOffset: "2px",
            expandedContentPaddingTop: "4px",
            detailSectionGap: "12px",
            detailSectionMinWidth: "240px",
            detailBorder: `1px solid ${COLORS.border.main}`,
            detailHeaderGap: "12px",
            detailHeaderPadding: "10px 12px",
            detailHeaderBackground: COLORS.checkout.lightBg,
            detailHeaderAccentWidth: "4px",
            detailHeaderFontWeight: 700,
            detailTagFontSize: "11px",
            detailTagFontWeight: 700,
            detailTagLetterSpacing: "0.06em",
            detailBodyPadding: "12px",
            detailLabelFontSize: "11px",
            detailLabelLetterSpacing: "0.06em",
            detailLabelMarginBottom: "10px",
            detailRowGap: "12px",
            detailRowMarginBottom: "6px",
            detailTotalPaddingTop: "12px",
            detailTotalBorder: `1px solid ${COLORS.border.main}`,
            detailTotalFontWeight: 700,
            finalTotalGap: "12px",
            finalTotalMarginTop: "12px",
            finalTotalPaddingTop: "12px",
            finalTotalBorder: `1px solid ${COLORS.border.main}`,
            finalTotalFontWeight: 700,
            finalTotalAmountFontSize: "18px",
            additionalMessagePaddingTop: "12px",
            additionalMessageBorder: `1px solid ${COLORS.border.main}`,
            additionalMessageFontSize: "12px",
        },
        termsMethod: {
            background: COLORS.checkout.lightBg,
            border: `1px solid ${COLORS.border.main}`,
            borderRadius: "4px",
            gap: "4px",
            padding: "14px 16px",
        },
        termsMethodLabel: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodValue: {
            color: COLORS.checkout.text.primary,
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodHelper: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            lineHeight: "1.3",
        },
    },
    announcementBar: {
        defaultBackground: "#000000",
        defaultTextColor: "#FFFFFF",
    },
    imageGrid: {
        imageWidth: [12, 12, 12, 6, 6] as const,
        contentWidth: [12, 12, 12, 6, 6] as const,
        imageMaxWidth: "426px",
        contentMaxWidth: "580px",
        imageFlexBasis: "42%",
        contentFlexBasis: "57%",
        padding: {
            left: "70px",
            right: "70px",
        },
        mobileXPadding: "0",
        image: {
            aspectRatio: "2/1",
        },
        contentTitle: {
            variant: "h3" as const,
            fontSize: "24px",
            responsiveFontSizes: ["20px", "20px", "22px", null, null],
        },
        preTitle: {
            letterSpacing: "normal",
        },
    },
    featuredCategory: {
        slideTitle: {
            weight: 700,
            textTransform: "capitalize",
        },
    },
    heroCarousel: {
        slideTitle: {
            size: "36px",
            lineHeight: "48px",
        },
    },
    tabGroup: {
        wrapper: {
            padding: "4px",
        },
    },
    productListDrawer: {
        size: 350,
        width: undefined,
        top: undefined,
        title: {
            letterSpacing: "normal",
        },
    },
};

export const Z_INDEX = {
    popover: 1260,
    datePicker: 1270,
    dynamicDropdown: 1280,
    stickyFooter: 1284,
    loadingOverlay: 1285,
    menu: 1290,
    drawer: 1290,
    modal: 1300,
    tabGroup: 400,
    toaster: 1400,
    shellPopover: 1310,
};

export const COMPONENTS = {
    pagination: {
        defaultProps: {
            navIconsSrc: {
                firstPage: "ChevronsLeft",
                previousPage: "ChevronLeft",
                nextPage: "ChevronRight",
                lastPage: "ChevronsRight",
            },
        },
    },
};
